/**
 *  apiV1.js
 *  @description: Cliente de la API v1 (api-go). Autentica con
 *  `Authorization: Bearer <JWT>` y desenvuelve el envelope
 *  `{success, message:{en,es}, data, schema, error}`.
 *
 *  Todas las funciones regresan un objeto con la misma forma:
 *    { ok, status, data, message, schema }
 *  y nunca lanzan: un error de red o del servidor llega con ok = false.
 */
import request from 'superagent';
import { URL_API } from './urlsApi';

const PREFIX = 'api/v1/';
const SESSION_KEY = 'bdusr';

// --- sesión ---
// Se guarda en la misma llave que usaba la web ('bdusr') con `auth`, para
// que PrivateRoute siga funcionando sin cambios.

export function getSession() {
   try {
      return JSON.parse(window.localStorage.getItem(SESSION_KEY)) || { auth: false };
   } catch (e) {
      return { auth: false };
   }
}

export function saveSession(session) {
   window.localStorage.setItem(
      SESSION_KEY,
      JSON.stringify({
         auth: true,
         token: session.token,
         expiresAt: session.expiresAt,
         user: session.user,
      })
   );
}

export function clearSession() {
   window.localStorage.setItem(SESSION_KEY, '{"auth": false}');
   window.localStorage.removeItem('menusr');
   window.localStorage.removeItem('menuActivo');
   window.localStorage.removeItem('menuParent');
}

export function token() {
   const s = getSession();
   if (!s.auth || !s.token) return '';
   // Un JWT vencido se descarta antes de mandarlo.
   if (s.expiresAt && new Date(s.expiresAt) <= new Date()) return '';
   return s.token;
}

// Rutas donde un 401 es una respuesta normal (credenciales incorrectas) y no
// debe cerrar la sesión.
const AUTH_PATHS = ['auth/web/login', 'auth/activate', 'auth/password-recovery'];

function onUnauthorized(path) {
   if (AUTH_PATHS.some((p) => path.startsWith(p))) return;
   clearSession();
   if (window.location.pathname !== '/login') {
      window.location = '/login';
   }
}

function result(res) {
   const body = res.body || {};
   return {
      ok: !!body.success,
      status: res.status,
      data: body.data,
      message: (body.message && (body.message.es || body.message.en)) || '',
      schema: body.schema || null,
   };
}

async function send(method, path, { query, body, files, fields, bearer } = {}) {
   let req = request(method, URL_API + PREFIX + path);
   const auth = bearer !== undefined ? bearer : token();
   if (auth) req = req.set('Authorization', 'Bearer ' + auth);
   if (query) req = req.query(query);
   if (files || fields) {
      Object.entries(fields || {}).forEach(([k, v]) => {
         if (v !== undefined && v !== null) req = req.field(k, typeof v === 'object' ? JSON.stringify(v) : String(v));
      });
      (files || []).forEach(([field, file]) => {
         if (file) req = req.attach(field, file);
      });
   } else if (body !== undefined) {
      req = req.send(body);
   }
   try {
      return result(await req);
   } catch (err) {
      if (err.response) {
         if (err.response.status === 401) onUnauthorized(path);
         const r = result(err.response);
         if (!r.message) r.message = schemaMessage(r.schema) || 'Ocurrió un error, intenta de nuevo.';
         return r;
      }
      return { ok: false, status: 0, data: null, message: 'No hay conexión con el servidor.', schema: null };
   }
}

// schemaMessage arma un texto legible con los errores por campo.
export function schemaMessage(schema) {
   if (!schema) return '';
   return Object.entries(schema)
      .map(([field, errs]) => `${field}: ${[].concat(errs).join(', ')}`)
      .join(' · ');
}

// download baja un archivo (Excel) con la sesión y lo guarda con el nombre
// que manda el servidor.
export async function download(path, query, fallbackName) {
   try {
      const res = await request
         .get(URL_API + PREFIX + path)
         .query(query || {})
         .set('Authorization', 'Bearer ' + token())
         .responseType('blob');
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="([^"]+)"/);
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(res.body);
      link.download = match ? match[1] : fallbackName;
      link.click();
      return { ok: true };
   } catch (err) {
      if (err.response && err.response.status === 401) onUnauthorized(path);
      return { ok: false, message: 'No se pudo descargar el archivo.' };
   }
}

const api = {
   get: (path, query) => send('GET', path, { query }),
   post: (path, body, opts) => send('POST', path, { body, ...opts }),
   put: (path, body) => send('PUT', path, { body }),
   patch: (path, body) => send('PATCH', path, { body }),
   del: (path, body) => send('DELETE', path, { body }),
   // multipart: files = [[campo, File], ...], fields = {campo: valor}
   upload: (method, path, files, fields) => send(method, path, { files, fields }),
};

export default api;
